import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@quest-lock/ui/button'
import { useAuth0 } from '@auth0/auth0-react'
import { Menu, X, LogIn, LogOut, Key, Asterisk } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appRoutes } from '@/constants'

interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  requiresAuth?: boolean
}

const navItems: NavItem[] = [
  { label: 'Authenticator', href: appRoutes.home, icon: Key },
  {
    label: 'Password Generator',
    href: appRoutes.passwordGenerator,
    icon: Asterisk
  }
]

export const MobileNavDropdown = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const location = useLocation()
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0()

  const logoutWithRedirect = () =>
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    })

  const filteredNavItems = navItems.filter(
    item => !item.requiresAuth || isAuthenticated
  )

  const openMenu = () => {
    setIsVisible(true)
    setTimeout(() => setIsOpen(true), 10)
  }

  const closeMenu = () => {
    setIsOpen(false)
    setTimeout(() => setIsVisible(false), 200)
  }

  useEffect(() => {
    closeMenu()
  }, [location.pathname])

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isVisible])

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="icon"
        className="rounded-full"
        onClick={isVisible ? closeMenu : openMenu}
        aria-expanded={isOpen}
        aria-label="Toggle navigation menu"
      >
        {isVisible ? <X className="size-4" /> : <Menu className="size-4" />}
      </Button>

      {isVisible && (
        <>
          <div
            className={cn(
              'fixed inset-0 z-[99] bg-black/20 backdrop-blur-sm transition-all duration-200',
              isOpen ? 'opacity-100' : 'opacity-0'
            )}
            onClick={closeMenu}
          />

          <div
            className={cn(
              'fixed top-28 right-4 left-4 z-[100] transition-all duration-200',
              isOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
            )}
          >
            <div className="border-shire-bark/30 from-shire-light/95 rounded-2xl border-2 border-dashed bg-gradient-to-br to-white/95 p-4 shadow-xl backdrop-blur-sm">
              <nav className="space-y-1">
                {filteredNavItems.map(item => {
                  const Icon = item.icon
                  const isActive = location.pathname === item.href

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={closeMenu}
                      className={cn(
                        'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200',
                        isActive
                          ? 'bg-shire-sun/20 text-shire-dark ring-shire-sun/30 ring-2'
                          : 'text-shire-bark hover:text-shire-dark hover:bg-white/60'
                      )}
                    >
                      <Icon className="size-5 shrink-0" />
                      <span>{item.label}</span>
                      {isActive && (
                        <div className="bg-shire-sun ml-auto size-2 rounded-full" />
                      )}
                    </Link>
                  )
                })}
              </nav>

              <div className="border-shire-bark/20 my-4 border-t" />

              <div className="space-y-2">
                {isLoading ? (
                  <div className="h-10 w-full animate-pulse rounded-xl bg-gray-200" />
                ) : (
                  <>
                    {!isAuthenticated ? (
                      <>
                        <Button
                          variant="ghost"
                          className="w-full justify-start gap-3 rounded-xl px-4 py-3 font-medium"
                          onClick={() => {
                            closeMenu()
                            loginWithRedirect()
                          }}
                        >
                          <LogIn className="size-5" />
                          Sign In
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        className="w-full justify-start gap-3 rounded-xl px-4 py-3 font-medium text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => {
                          closeMenu()
                          logoutWithRedirect()
                        }}
                      >
                        <LogOut className="size-5" />
                        Sign Out
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
